import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Edit, Trash2, Star, Users, X } from "lucide-react";
import { SortableHead, useSortedRows } from "@/components/SortableHeader";

const ACCESSORS = {
  name: (c) => c.name,
  email: (c) => c.email,
  phone: (c) => c.phone,
  address: (c) => c.address,
  contacts: (c) => (c.contacts || []).length,
};

const collectStrings = (value, out) => {
  if (value == null) return out;
  if (typeof value === "string" || typeof value === "number") out.push(String(value));
  else if (Array.isArray(value)) value.forEach((v) => collectStrings(v, out));
  else if (typeof value === "object") Object.entries(value).forEach(([k, v]) => k !== "id" && k !== "created_at" && collectStrings(v, out));
  return out;
};

export const matchesCustomerSearch = (customer, query) => {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const haystack = collectStrings(customer, []).join(" ").toLowerCase();
  return terms.every((t) => haystack.includes(t));
};

const primaryContact = (customer) =>
  (customer.contacts || []).find((c) => c.is_primary) || (customer.contacts || [])[0];

export const CustomerTable = ({ customers, onEdit, onDelete, canDelete, onSelect }) => {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => customers.filter((c) => matchesCustomerSearch(c, query)), [customers, query]);
  const { sorted, sort, toggle } = useSortedRows(filtered, ACCESSORS, "name");

  return (
    <Card className="bg-white border-0 shadow-lg" data-testid="customer-table-card">
      <CardContent className="p-0">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border-b border-slate-100">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, phone, address, contacts…"
              className="pl-9 pr-9"
              data-testid="customer-search-input"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="customer-search-clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <p className="text-sm text-slate-500 whitespace-nowrap" data-testid="customer-result-count">
            {filtered.length} of {customers.length} customers
          </p>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="customer-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <SortableHead label="Name" sortKey="name" sort={sort} onToggle={toggle} />
                <SortableHead label="Email" sortKey="email" sort={sort} onToggle={toggle} />
                <SortableHead label="Phone" sortKey="phone" sort={sort} onToggle={toggle} />
                <SortableHead label="Address" sortKey="address" sort={sort} onToggle={toggle} className="hidden lg:table-cell" />
                <SortableHead label="Contacts" sortKey="contacts" sort={sort} onToggle={toggle} />
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-16 text-center" data-testid="customer-table-empty">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600 font-medium">No customers match “{query}”</p>
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((customer) => {
                  const contact = primaryContact(customer);
                  return (
                    <TableRow key={customer.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => onSelect && onSelect(customer)} data-testid={`customer-row-${customer.id}`}>
                      <TableCell className="font-semibold text-slate-800">{customer.name}</TableCell>
                      <TableCell className="text-slate-600">
                        <a href={`mailto:${customer.email}`} className="hover:text-blue-600" onClick={(e) => e.stopPropagation()}>{customer.email}</a>
                      </TableCell>
                      <TableCell className="text-slate-600 whitespace-nowrap">{customer.phone}</TableCell>
                      <TableCell className="hidden lg:table-cell text-slate-600 max-w-xs truncate" title={customer.address}>{customer.address}</TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {contact ? (
                          <span className="inline-flex items-center gap-1">
                            {contact.is_primary && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />}
                            {contact.name}{contact.title ? ` · ${contact.title}` : ""}
                            {customer.contacts.length > 1 && <Badge variant="outline" className="ml-1 text-xs">+{customer.contacts.length - 1}</Badge>}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <Button size="icon" variant="ghost" onClick={() => onEdit(customer)} className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50" data-testid="edit-customer-button">
                          <Edit className="w-4 h-4" />
                        </Button>
                        {canDelete && (
                          <Button size="icon" variant="ghost" onClick={() => onDelete(customer.id)} className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="delete-customer-button">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
